import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Store, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  User,
  Link2,
  AlertCircle,
  RefreshCw,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
import { CA_BASE } from '@/utils/getBasePath';
const ca = (route = '') => route ? `${CA_BASE}/${route}` : (CA_BASE || '/');

// Get Company Admin token
const getCompanyAdminToken = () => {
  return localStorage.getItem('company_admin_token');
};

const statusConfig = {
  new: { label: 'New', color: 'bg-blue-500', icon: Clock },
  reviewed: { label: 'Reviewed', color: 'bg-yellow-500', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle },
};

export default function OnboardingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form states
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('normal');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const fetchEvent = useLatestCallback(async () => {
    setLoading(true);
    try {
      const token = getCompanyAdminToken();
      const res = await fetch(`${API}/api/admin/onboarding/restaurants/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEvent(data);
        setNotes(data.notes || '');
        setPriority(data.priority || 'normal');
      } else if (res.status === 404) {
        toast.error('Onboarding event not found');
        navigate(ca('onboarding'));
      }
    } catch (err) {
      console.error('Failed to fetch event:', err);
      toast.error('Failed to load details');
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchEvent();
  }, [id, fetchEvent]);

  const handleAction = async (action, extraData = {}) => {
    setActionLoading(true);
    try {
      const token = getCompanyAdminToken();
      const endpoint = action === 'mark-reviewed' ? 'mark-reviewed' : action;
      
      const res = await fetch(`${API}/api/admin/onboarding/restaurants/${id}/${endpoint}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notes: notes || undefined,
          priority: priority || undefined,
          ...extraData
        })
      });
      
      if (res.ok) {
        toast.success(`Successfully ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'marked as reviewed'}`);
        fetchEvent();
        if (action === 'reject') setShowRejectDialog(false);
      } else {
        toast.error('Action failed');
      }
    } catch (err) {
      console.error('Action error:', err);
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Event not found</p>
        <Link to={ca('onboarding')}>
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Inbox
          </Button>
        </Link>
      </div>
    );
  }

  const statusInfo = statusConfig[event.status] || statusConfig.new;
  const StatusIcon = statusInfo.icon;
  const isActionable = event.status === 'new' || event.status === 'reviewed';

  return (
    <div className="space-y-6" data-testid="onboarding-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={ca('onboarding')}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{event.restaurant_name}</h1>
            <p className="text-muted-foreground">Restaurant #{event.restaurant_id}</p>
          </div>
        </div>
        <Badge variant="outline" className={`${statusInfo.color} text-white text-sm px-3 py-1`}>
          <StatusIcon className="w-4 h-4 mr-1" />
          {statusInfo.label}
        </Badge>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Owner Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Owner Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{event.owner_email}</p>
                  </div>
                </div>
                {event.owner_phone_e164 && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{event.owner_phone_e164}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Restaurant Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5" />
                Restaurant Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Restaurant Name</p>
                  <p className="font-medium">{event.restaurant_name}</p>
                </div>
                {event.restaurant_slug && (
                  <div className="flex items-center gap-3">
                    <Link2 className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Slug</p>
                      <p className="font-medium">{event.restaurant_slug}</p>
                    </div>
                  </div>
                )}
                {event.phone && (
                  <div>
                    <p className="text-sm text-muted-foreground">Business Phone</p>
                    <p className="font-medium">{event.phone}</p>
                  </div>
                )}
              </div>
              
              {(event.address_line1 || event.city) && (
                <div className="flex items-start gap-3 pt-2 border-t">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium">
                      {event.address_line1 && <>{event.address_line1}<br /></>}
                      {[event.city, event.state, event.zip].filter(Boolean).join(', ')}
                      {event.country && <><br />{event.country}</>}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <div>
                    <p className="font-medium">Signup Received</p>
                    <p className="text-sm text-muted-foreground">{formatDate(event.created_at)}</p>
                  </div>
                </div>
                {event.reviewed_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div>
                      <p className="font-medium">Marked as Reviewed</p>
                      <p className="text-sm text-muted-foreground">{formatDate(event.reviewed_at)}</p>
                    </div>
                  </div>
                )}
                {event.approved_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <div>
                      <p className="font-medium">Approved</p>
                      <p className="text-sm text-muted-foreground">{formatDate(event.approved_at)}</p>
                    </div>
                  </div>
                )}
                {event.rejected_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div>
                      <p className="font-medium">Rejected</p>
                      <p className="text-sm text-muted-foreground">{formatDate(event.rejected_at)}</p>
                      {event.reject_reason && (
                        <p className="text-sm text-red-600 mt-1">Reason: {event.reject_reason}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Actions */}
        <div className="space-y-6">
          {/* Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
              <CardDescription>Review and manage this signup</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Priority */}
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={priority} 
                  onValueChange={setPriority}
                  disabled={!isActionable}
                >
                  <SelectTrigger data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Textarea
                  placeholder="Add notes for your team..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  disabled={!isActionable}
                  data-testid="notes-textarea"
                />
              </div>

              {/* Action Buttons */}
              {isActionable && (
                <div className="space-y-2 pt-4 border-t">
                  {event.status === 'new' && (
                    <Button 
                      className="w-full"
                      variant="outline"
                      onClick={() => handleAction('mark-reviewed')}
                      disabled={actionLoading}
                      data-testid="mark-reviewed-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Mark as Reviewed
                    </Button>
                  )}
                  
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => handleAction('approve')}
                    disabled={actionLoading}
                    data-testid="approve-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  
                  <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
                    <DialogTrigger asChild>
                      <Button 
                        className="w-full"
                        variant="destructive"
                        disabled={actionLoading}
                        data-testid="reject-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Reject Signup</DialogTitle>
                        <DialogDescription>
                          Please provide a reason for rejecting this restaurant signup.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Reason *</Label>
                          <Textarea
                            placeholder="Enter rejection reason..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={3}
                            data-testid="reject-reason-textarea"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                          Cancel
                        </Button>
                        <Button 
                          variant="destructive"
                          onClick={() => handleAction('reject', { reason: rejectReason })}
                          disabled={!rejectReason.trim() || actionLoading}
                          data-testid="confirm-reject-btn"
                        >
                          Confirm Reject
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {/* Status Message for Completed */}
              {!isActionable && (
                <div className={`p-4 rounded-lg ${event.status === 'approved' ? 'bg-green-50 text-green-700 dark:bg-green-950' : 'bg-red-50 text-red-700 dark:bg-red-950'}`}>
                  <div className="flex items-center gap-2">
                    {event.status === 'approved' ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <XCircle className="w-5 h-5" />
                    )}
                    <span className="font-medium">
                      {event.status === 'approved' ? 'This signup has been approved' : 'This signup has been rejected'}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes Display */}
          {event.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{event.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
